import { AssetChecklist } from "@/components/assets/AssetChecklist";
import { AssetContacts } from "@/components/assets/AssetDetail/AssetContacts";
import AssetDetailsOverview from "@/components/assets/AssetDetail/AssetDetailsOverview";
import AssetDocuments from "@/components/assets/AssetDetail/AssetDocuments";
import { AssetFinancialsTab } from "@/components/assets/AssetDetail/AssetFinancials";
import { AssetPhotos } from "@/components/assets/AssetDetail/AssetPhotos";
import { AssetTransactionsTab } from "@/components/assets/AssetDetail/AssetTransactions";
import { RentalKpiDashboard } from "@/components/assets/AssetDetail/RentalKpiDashboard";
import EditAssetForm from "@/components/assets/EditAssetForm";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { AssetFormValues } from "@/lib/schemas";
import assetService from "@/services/AssetService";
import type { Asset } from "@/types/asset";
import { Edit, Loader, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

interface Contact {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  slug: string;
  role: string;
}

interface AssetWithRelations extends Asset {
  contacts?: Contact[];
  entity?: {
    id: number;
    name: string;
    slug: string;
  };
}

interface AssetDetailPageProps {
  isEditing?: boolean;
  assetFromWrapper?: Asset;
}

export type AssetChatContext =
  | {
      type: "property";
      assetId: number;
      assetName: string;
    }
  | undefined;

export default function AssetDetailPage({
  isEditing = false,
  assetFromWrapper,
}: AssetDetailPageProps) {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [asset, setAsset] = useState<AssetWithRelations | null>(
    assetFromWrapper ?? null
  );
  const [isLoading, setIsLoading] = useState(!assetFromWrapper);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [showEditForm, setShowEditForm] = useState(isEditing);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [openAddTransactionModal, setOpenAddTransactionModal] = useState(false);
  const [initialTransactionCategory, setInitialTransactionCategory] = useState<
    string | undefined
  >(undefined);
  const [openAddFinancialModal, setOpenAddFinancialModal] = useState(false);
  const [initialFinancialCategory, setInitialFinancialCategory] = useState<
    string | undefined
  >(undefined);

  // Function to fetch asset details
  const fetchAssetDetails = useCallback(async () => {
    if (!slug) return;
    setIsLoading(true);
    setError(null);
    try {
      // First get assets to find the ID from slug
      const assets = await assetService.getAssets();
      const matchedAsset = assets.find((a) => a.slug === slug);

      if (!matchedAsset) {
        setError("Asset not found");
        setIsLoading(false);
        return;
      }

      // Then fetch the complete asset details using the ID
      const data = await assetService.getAsset(matchedAsset.id);
      setAsset(data as AssetWithRelations);

      // If we're in edit mode, show the edit form automatically
      if (isEditing) {
        setShowEditForm(true);
      }
    } catch (err) {
      console.error("Error fetching asset details:", err);
      setError("Failed to load asset details. Please try again later.");
      toast.error("Failed to load asset details.");
    } finally {
      setIsLoading(false);
    }
  }, [slug, isEditing]);

  // If assetFromWrapper is provided, skip fetching. Otherwise, fetch as before (for direct navigation, fallback, or edit mode)
  useEffect(() => {
    if (assetFromWrapper) return;
    fetchAssetDetails();
  }, [assetFromWrapper, fetchAssetDetails]);

  const handleDelete = async () => {
    if (!asset) return;

    try {
      await assetService.deleteAsset(asset.id);
      toast.success("Asset has been deleted successfully.");
      navigate("/assets");
    } catch (err) {
      console.error("Error deleting asset:", err);
      toast.error("Failed to delete asset. Please try again later.");
    }
  };

  const handleEdit = () => {
    setShowEditForm(true);
  };

  const handleAssetUpdated = (updatedAsset: Asset) => {
    setAsset({ ...asset, ...updatedAsset } as AssetWithRelations);
    setShowEditForm(false);
    toast.success("Asset has been updated successfully.");
  };

  const handleViewMoreTransactions = () => setActiveTab("transactions");

  // Handler for adding transactions from empty state
  const handleAddTransaction = (categoryMetadata?: string) => {
    setActiveTab("transactions");
    setInitialTransactionCategory(categoryMetadata);
    setOpenAddTransactionModal(true);
  };

  // Handler for adding financial records
  const handleAddFinancial = (categoryMetadata?: string) => {
    setActiveTab("financials");
    setInitialFinancialCategory(categoryMetadata);
    setOpenAddFinancialModal(true);
  };

  // Function to refresh asset data (e.g., after transaction uploads)
  const handleAssetDataRefresh = useCallback(() => {
    fetchAssetDetails();
  }, [fetchAssetDetails]);

  // Reset openAddTransactionModal when transactions tab becomes active
  useEffect(() => {
    if (activeTab === "transactions" && openAddTransactionModal) {
      // Reset after a short delay to allow the component to mount
      const timer = setTimeout(() => {
        setOpenAddTransactionModal(false);
        setInitialTransactionCategory(undefined);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [activeTab, openAddTransactionModal]);

  // Reset openAddFinancialModal when financials tab becomes active
  useEffect(() => {
    if (activeTab === "financials" && openAddFinancialModal) {
      // Reset after a short delay to allow the component to mount
      const timer = setTimeout(() => {
        setOpenAddFinancialModal(false);
        setInitialFinancialCategory(undefined);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [activeTab, openAddFinancialModal]);

  // Format asset type for display
  const formatAssetType = (type: string): string => {
    return type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 flex justify-center items-center min-h-[400px]">
        <Loader className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !asset) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center py-12 border rounded-lg bg-muted/50">
          <h3 className="text-lg font-medium">Error</h3>
          <p className="text-muted-foreground mt-2">
            {error || "Asset not found"}
          </p>
          <Button
            onClick={() => navigate("/assets")}
            variant="outline"
            className="mt-4"
          >
            Back to Assets
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-3xl font-bold">
            {showEditForm ? `Edit ${asset.name}` : asset.name}
          </h1>
          {asset.address && (
            <p className="text-muted-foreground mt-1">{asset.address}</p>
          )}
          <div className="flex gap-2 mt-2">
            <span className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-full">
              {formatAssetType(asset.asset_type)}
            </span>
            <span
              className={`text-xs px-2 py-1 rounded-full capitalize ${
                asset.investment_status === "active"
                  ? "bg-green-100 text-green-700"
                  : asset.investment_status === "pending"
                    ? "bg-amber-100 text-amber-700"
                    : asset.investment_status === "sold"
                      ? "bg-blue-100 text-blue-700"
                      : "bg-gray-100 text-gray-700"
              }`}
            >
              {asset.investment_status}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          {!showEditForm && (
            <>
              <Button variant="outline" onClick={handleEdit}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <Button
                variant="destructive"
                onClick={() => setShowDeleteDialog(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Edit Form */}
      {showEditForm ? (
        <EditAssetForm
          asset={asset}
          onCancel={() => setShowEditForm(false)}
          onUpdate={handleAssetUpdated}
        />
      ) : (
        <div className="w-full">
          {/* Tabs */}
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <TabsList className="mb-4">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="financials">Financials</TabsTrigger>
              <TabsTrigger value="transactions">Transactions</TabsTrigger>
              <TabsTrigger value="contacts">Contacts</TabsTrigger>
              <TabsTrigger value="documents">Documents</TabsTrigger>
              <TabsTrigger value="photos">Photos</TabsTrigger>
            </TabsList>
            <TabsContent value="overview">
              <AssetDetailsOverview
                assetId={asset.id}
                asset={{
                  // @ts-expect-error: provided by backend
                  netYield: asset.net_yield ?? 0,
                  // @ts-expect-error: provided by backend
                  cashFlow: asset.cash_flow ?? 0,
                  // @ts-expect-error: provided by backend
                  occupancy: asset.occupancy ?? 0,
                  // Add the invested amount using purchase_price field
                  investedAmount: asset.purchase_price ?? 0,
                  // Add market rent and market value
                  marketRent: asset.market_rent ?? 0,
                  marketValue: asset.market_value ?? 0,
                  // Add asset type for checklist
                  assetType: asset.asset_type,
                  // @ts-expect-error: provided by backend
                  transactions: asset.transactions ?? [],
                  // @ts-expect-error: provided by backend
                  performanceData: asset.performance_data ?? [],
                }}
                assetData={asset} // Pass the full asset data
                onViewMoreTransactions={handleViewMoreTransactions}
                onAddTransaction={handleAddTransaction}
                onEditAsset={handleEdit} // Pass the edit handler
              />
            </TabsContent>
            <TabsContent value="financials">
              <AssetFinancialsTab
                assetId={asset.id}
                openAddModal={openAddFinancialModal}
                initialCategory={initialFinancialCategory}
              />
            </TabsContent>
            <TabsContent value="transactions">
              <AssetTransactionsTab
                assetId={asset.id}
                onAssetDataRefresh={handleAssetDataRefresh}
                openAddModal={openAddTransactionModal}
                initialCategory={initialTransactionCategory}
              />
            </TabsContent>
            <TabsContent value="contacts">
              <AssetContacts assetId={asset.id} />
            </TabsContent>
            <TabsContent value="documents">
              <AssetDocuments assetId={asset.id} />
            </TabsContent>
            <TabsContent value="photos">
              <AssetPhotos assetId={asset.id} />
            </TabsContent>
          </Tabs>
        </div>
      )}

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action will remove "{asset.name}" from your portfolio. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Floating Checklist */}
      <AssetChecklist
        variant="floating"
        asset={asset}
        onItemToggle={(itemId, completed) => {
          // Store in localStorage for persistence
          const key = `asset-checklist-${asset.id}`;
          const existing = JSON.parse(localStorage.getItem(key) || "{}");
          existing[itemId] = completed;
          localStorage.setItem(key, JSON.stringify(existing));
          toast.success(`Checklist updated: ${itemId}`);
        }}
        onActionClick={(action, itemId, categoryMetadata) => {
          switch (action) {
            case "edit_asset":
              handleEdit();
              break;
            case "add_transaction":
              handleAddTransaction(categoryMetadata);
              break;
            case "add_financial":
              handleAddFinancial(categoryMetadata);
              break;
            case "view_transactions":
              setActiveTab("transactions");
              break;
            case "view_documents":
              setActiveTab("documents");
              break;
          }
        }}
      />
    </div>
  );
}
